import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
const BACKEND_URL = process.env.REACT_APP_API_URL;
interface League {
    leagueId: number;
    leagueName: string;
    venue: string;
    city: string;
    country: string;
    teamFormat: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
    createdAt: string;
    playerCount: number;
    teamCount: number;
    moneyEarned: number;
    moneySpent: number;
    totalBalance: number;
    topEarned: { playerId: number; player_name: string; totalEarned: number } | null;
    topSpender: { playerId: number; player_name: string; totalSpent: number } | null;
}

interface SideEventEarning {
    sideEventId: number;
    sideEventName: string;
    sideEventType: string;
    appliesTo: string;
    totalEarned: number;
}

interface TopBowler {
    bowlerId: number;
    bowlerName: string;
    avgScore: number;
    totalPayout: number;
}

interface TopTeam {
    teamId: number;
    teamName: string;
    teamFormat: string;
    avgScore: number;
    totalPayout: number;
}

interface TopTeamsByFormat {
    individual: TopTeam[];
    duos: TopTeam[];
    trios: TopTeam[];
    squads: TopTeam[];
    fives: TopTeam[];
}

interface AnalyticsData {
    admin: {
        adminId: number;
        adminName: string;
    };
    leagueSummary: {
        totalLeagues: number;
        teamFormatCounts: { teamFormat: string; count: number }[];
    };
    leagues: League[];
    sideEventEarnings: SideEventEarning[];
    topBowlers: TopBowler[];
    topTeamsByFormat: TopTeamsByFormat;
}

const Analytics: React.FC = () => {
    const [data, setData] = useState<AnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    // Function to translate team format
    const getTeamFormatTranslation = (format: string): string => {
        const formatLower = format.toLowerCase();
        const formatMap: { [key: string]: string } = {
            'individual': 'league.formatIndividual',
            'duos': 'league.formatDuos',
            'duo': 'league.formatDuos',
            'trios': 'league.formatTrios',
            'squads': 'league.formatSquads',
            'fives': 'league.formatFives',
        };
        return t(formatMap[formatLower] || format);
    };

    // Function to get the full "leagues + format" translation for badges
    const getLeaguesFormatLabel = (format: string): string => {
        const formatLower = format.toLowerCase();
        const leaguesFormatMap: { [key: string]: string } = {
            'individual': 'admin.analytics.leaguesIndividual',
            'duos': 'admin.analytics.leaguesDuos',
            'duo': 'admin.analytics.leaguesDuos',
            'trios': 'admin.analytics.leaguesTrios',
            'squads': 'admin.analytics.leaguesSquads',
            'fives': 'admin.analytics.leaguesFives',
        };
        return t(leaguesFormatMap[formatLower] || `${t('admin.analytics.leagues')} ${format}`);
    };

    // Function to translate side event types
    const getSideEventTypeTranslation = (type: string): string => {
        const typeMap: { [key: string]: string } = {
            'bracket': t('admin.analytics.sideEventTypes.bracket'),
            'high_game': t('admin.analytics.sideEventTypes.highGame'),
            'high_series': t('admin.analytics.sideEventTypes.highSeries'),
            'mystery_score': t('admin.analytics.sideEventTypes.mysteryScore'),
            'mystery_series': t('admin.analytics.sideEventTypes.mysterySeries'),
            'clean_game': t('admin.analytics.sideEventTypes.cleanGame'),
            'side_pots': t('admin.analytics.sideEventTypes.sidePots'),
            'survivor': t('admin.analytics.sideEventTypes.survivor'),
            'eliminator': t('admin.analytics.sideEventTypes.eliminator'),
        };
        return typeMap[type.toLowerCase()] || type.replace(/_/g, ' ');
    };

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            try {
                const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
                const res = await fetch(`${apiUrl}/admin/analytics`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                
                if (!res.ok) {
                    if (res.status === 401) {
                        console.error('Unauthorized: Token may be invalid or expired');
                        // Optionally redirect to login
                        navigate('/login');
                    } else if (res.status === 500) {
                        console.error('Server error: Failed to fetch analytics data');
                    }
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                
                const json = await res.json();
                setData(json);
            } catch (error) {
                console.error('Failed to fetch analytics:', error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
    }, []);

    const [openTypes, setOpenTypes] = useState<{ [type: string]: boolean }>({});
    const navigate = useNavigate();
    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
                <div className="text-red-400 text-xl font-bold">{t('admin.analytics.failedToLoad')}</div>
            </div>
        );
    }

    const filteredSideEvents = data.sideEventEarnings.filter(se => se.totalEarned > 0);
    const totalSideEventEarnings = filteredSideEvents.reduce((acc, se) => acc + Number(se.totalEarned), 0);

    const totalLeaguesEarnings = data.leagues.reduce((sum, l) => sum + l.totalBalance, 0);

    const groupedByType: { [type: string]: SideEventEarning[] } = {};
    filteredSideEvents.forEach(se => {
        if (!groupedByType[se.sideEventType]) groupedByType[se.sideEventType] = [];
        groupedByType[se.sideEventType].push(se);
    });


    const toggleType = (type: string) => {
        setOpenTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
                <div className="text-white text-xl font-bold">{t('admin.analytics.loadingAnalytics')}</div>
            </div>
        );
    }




    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 py-10 px-4">
            <div className="max-w-full mx-auto space-y-10">


                {/* Admin Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-white hover:text-blue-300 transition duration-200"
                            aria-label="Go back"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h1 className="text-3xl font-bold text-white mb-0">{t('admin.analytics.title')} 📊</h1>
                    </div>

                </div>
                {/* Leagues Table */}
                <div className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">{t('admin.analytics.leaguesOverview')}</h2>
                    <div className="flex flex-wrap gap-4 mt-2 mb-4 md:mt-0">
                        <div className="bg-white/10 text-white p-2 rounded-lg shadow border border-white/10">
                            <div className="text-xs uppercase tracking-wider text-white font-bold">{t('admin.analytics.totalLeagues')}: <span className="text-yellow-500 font-bold">{data.leagueSummary.totalLeagues}</span></div>
                        </div>
                        {data.leagueSummary.teamFormatCounts.map((tf) => (
                            <div key={tf.teamFormat} className="bg-white/10 text-white p-2 rounded-lg shadow border border-white/10">
                                <div className="text-xs uppercase tracking-wider text-white font-bold">{getLeaguesFormatLabel(tf.teamFormat)}: <span className="text-yellow-500 font-bold">{tf.count}</span></div>
                            </div>
                        ))}
                        <div className="bg-white/10 text-white p-2 rounded-lg shadow border border-white/10">
                            <div className="text-xs uppercase tracking-wider text-white font-bold">{t('admin.analytics.totalEarned')}: <span className="text-yellow-500 font-bold">${totalLeaguesEarnings}</span> 💵 </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-white">
                            <thead>
                                <tr className="bg-purple-800/60">
                                    <th className="px-4 py-2">{t('admin.analytics.name')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.venue')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.city')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.country')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.teamFormat')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.players')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.teams')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.earned')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.spent')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.balance')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.topEarner')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.topSpender')}</th>

                                    <th className="px-4 py-2">{t('admin.analytics.created')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.leagues.map((l) => (
                                    <tr key={l.leagueId} className="border-b border-purple-700/40 hover:bg-purple-800/30">
                                        <td className="px-4 py-2 font-bold">{l.leagueName}</td>
                                        <td className="px-4 py-2">{l.venue}</td>
                                        <td className="px-4 py-2">{l.city}</td>
                                        <td className="px-4 py-2">{l.country}</td>
                                        <td className="px-4 py-2">{getTeamFormatTranslation(l.teamFormat)}</td>
                                        <td className="px-4 py-2">{l.playerCount}</td>
                                        <td className="px-4 py-2">{l.teamCount}</td>
                                        <td className="px-4 py-2 text-green-300">${l.moneyEarned}</td>
                                        <td className="px-4 py-2 text-red-300">${l.moneySpent}</td>
                                        <td className={`px-4 py-2 font-bold ${l.totalBalance >= 0 ? "text-green-400" : "text-red-400"}`}>
                                            ${l.totalBalance}
                                        </td>
                                        <td className="px-4 py-2">
                                            {l.topEarned
                                                ? `${l.topEarned.player_name} ($${l.topEarned.totalEarned})`
                                                : "N/A"}
                                        </td>
                                        <td className="px-4 py-2">
                                            {l.topSpender
                                                ? `${l.topSpender.player_name} ($${l.topSpender.totalSpent})`
                                                : "N/A"}
                                        </td>

                                        <td className="px-4 py-2">{new Date(l.createdAt).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Side Event Earnings */}
                <div className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">{t('admin.analytics.sideEventEarnings')}</h2>
                    <div className="flex flex-wrap gap-2 mb-4">
                        <div className="text-white text-xs font-bold bg-white/10 border border-white/10 rounded p-2">
                            💰 {t('admin.analytics.totalSideEventEarnings')}: <span className="text-yellow-500 font-bold">${totalSideEventEarnings}</span>
                        </div>
                        {filteredSideEvents.length > 0 && (() => {
                            // Find the side event type with the most earned
                            const typeEarnings: { [type: string]: number } = {};
                            filteredSideEvents.forEach(se => {
                                typeEarnings[se.sideEventType] = (typeEarnings[se.sideEventType] || 0) + Number(se.totalEarned);
                            });
                            const mostPlayedType = Object.entries(typeEarnings).reduce((prev, curr) => 
                                curr[1] > prev[1] ? curr : prev
                            );
                            const leastPlayedType = Object.entries(typeEarnings).reduce((prev, curr) => 
                                curr[1] < prev[1] ? curr : prev
                            );
                            return (
                                <>
                                    <div className="text-white font-bold text-xs bg-green-500/20 border border-green-400/30 rounded p-2 flex items-center">
                                        📈 {t('admin.analytics.mostPlayed')}: <span className="text-green-300 ml-1">{getSideEventTypeTranslation(mostPlayedType[0])} (${mostPlayedType[1].toFixed(2)})</span>
                                    </div>
                                    <div className="text-white font-bold text-xs bg-red-500/20 border border-red-400/30 rounded p-2 flex items-center">
                                        📉 {t('admin.analytics.leastPlayed')}: <span className="text-red-300 ml-1">{getSideEventTypeTranslation(leastPlayedType[0])} (${leastPlayedType[1].toFixed(2)})</span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-white">
                            <thead>
                                <tr className="bg-purple-800/60">
                                    <th className="px-4 py-2">{t('admin.analytics.type')}</th>
                                    <th className="px-4 py-2">{t('admin.analytics.totalEarnedColumn')}</th>
                                    <th className="px-4 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupedByType).map(([type, events]) => {
                                    const sum = events.reduce((acc, se) => acc + Number(se.totalEarned), 0);
                                    return (
                                        <React.Fragment key={type}>
                                            <tr
                                                className="border-b border-purple-700/40 cursor-pointer hover:bg-purple-800/30"
                                                onClick={() => toggleType(type)}
                                            >
                                                <td className="px-4 py-2 font-bold">{getSideEventTypeTranslation(type)}</td>
                                                <td className="px-4 py-2 text-green-300 text-center">${sum}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <span className="text-purple-300">
                                                        {openTypes[type] ? "▲" : "▼"}
                                                    </span>
                                                </td>
                                            </tr>
                                            {openTypes[type] && events.map(se => (
                                                <tr key={se.sideEventId} className="bg-purple-800/20">
                                                    <td className="px-8 py-2">{se.sideEventName}</td>
                                                    <td className="px-4 py-2 text-green-200 text-right">${se.totalEarned}</td>
                                                    <td className="px-4 py-2 text-center">{se.appliesTo}</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Top Bowlers */}
                <div className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">{t('admin.analytics.topBowlers')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {data.topBowlers.map((b, idx) => (
                            <div key={b.bowlerId} className="bg-blue-800/40 rounded-lg p-4 flex flex-col items-center shadow border border-blue-500/30">
                                <div className="text-3xl font-bold text-green-300 mb-2">#{idx + 1}</div>
                                <div className="text-lg font-bold text-white">{b.bowlerName}</div>
                                <div className="text-purple-200 text-sm mb-1">
                                    {t('admin.analytics.avgScore')}: <span className="font-bold">
                                        {typeof b.avgScore === 'number' && !isNaN(b.avgScore)
                                            ? b.avgScore.toFixed(2)
                                            : 'N/A'}
                                    </span>
                                </div>
                                <div className="text-green-300 text-sm">{t('admin.analytics.totalPayout')}: ${b.totalPayout}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Teams by Format */}
                {Object.entries(data.topTeamsByFormat).map(([format, teams]) => {
                    if (teams.length === 0) return null;
                    return (
                        <div key={format} className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                            <h2 className="text-xl font-bold text-white mb-4">
                                {t('admin.analytics.topTeamsFormat', { format: getTeamFormatTranslation(format) })}
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {teams.map((team: TopTeam, idx: number) => (
                                    <div key={team.teamId} className="bg-blue-800/40 rounded-lg p-4 flex flex-col items-center shadow border border-blue-500/30">
                                        <div className="text-3xl font-bold text-green-300 mb-2">#{idx + 1}</div>
                                        <div className="text-lg font-bold text-white">{team.teamName}</div>
                                        <div className="text-purple-200 text-sm mb-1">
                                            {t('admin.analytics.avgScore')}: <span className="font-bold">
                                                {typeof team.avgScore === 'number' && !isNaN(team.avgScore)
                                                    ? team.avgScore.toFixed(2)
                                                    : 'N/A'}
                                            </span>
                                        </div>
                                        <div className="text-green-300 text-sm">{t('admin.analytics.totalPayout')}: ${team.totalPayout}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Analytics;